import {Component, DestroyRef, inject, Input, OnChanges, SimpleChanges} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {AnimalModel} from '../models/animal.model';
import {AnimalNoteExportModel, AnimalNoteExportPayload, AnimalNoteModel} from '../models/animal-note.model';
import {AnimalNoteRepository} from '../states/animal-note.repository';
import {ToastService} from 'src/app/shared/services/toast.service';
import {TranslocoService} from '@jsverse/transloco';
import {UserRepository} from "../../user/states/user.repository";
import {take} from 'rxjs/operators';
import {AssetService} from 'src/app/shared/services/asset.service';
import {HttpClient} from '@angular/common/http';

@Component({
  selector: 'app-animal-details-notes',
  templateUrl: './animal-details-notes.component.html',
  standalone: false
})
export class AnimalDetailsNotesComponent implements OnChanges {
  @Input() animal: AnimalModel | null = null;

  notes: AnimalNoteModel[] = [];
  exports: AnimalNoteExportModel[] = [];
  loading = false;
  loadingExports = false;
  saving = false;
  finalizing = false;

  searchTerm = '';

  newTitle = '';
  newDescription = '';
  createNoteDialogVisible = false;
  editNoteId: number | null = null;
  editTitle = '';
  editDescription = '';

  deleteNote: AnimalNoteModel | null = null;
  deleteDialogVisible = false;
  deleting = false;

  // Checkbox-Auswahl in der Liste (Sammel-Export).
  selectedNoteIds: number[] = [];
  // Notizen, die der aktuell geöffnete Export-Dialog tatsächlich verarbeitet.
  // Bewusst getrennt von selectedNoteIds, damit ein Einzel-Export die Listenauswahl nicht überschreibt.
  exportNoteIds: number[] = [];

  exportDialogVisible = false;
  renderSignaturePad = false;
  exportSignedAt = new Date();
  exportSignatureDataUrl: string | null = null;
  exportLockAcknowledged = false;
  pdfPreviewVisible = false;
  pdfPreviewLoading = false;
  pdfPreviewData: Blob | null = null;
  pdfPreviewTitle = '';
  pdfPreviewFileName = 'export.pdf';

  private readonly _lockedNoteIds = new Set<number>();
  private readonly _destroyRef = inject(DestroyRef);
  // Zuletzt geladene Tier-ID, um redundante Reloads bei gleicher ID (neue Objekt-Referenz) zu vermeiden.
  private _loadedAnimalId: number | null = null;

  constructor(
    private readonly _animalNoteRepository: AnimalNoteRepository,
    private readonly _toast: ToastService,
    private readonly _translateService: TranslocoService,
    private readonly _userRepository: UserRepository,
    private readonly _assetService: AssetService,
    private readonly _http: HttpClient
  ) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['animal']) {
      return;
    }

    const animalId = this.animal?.id ?? null;

    // Nur neu laden, wenn sich die Tier-ID wirklich geändert hat. Der Eltern-Component
    // weist `animal` teils mehrfach mit derselben ID neu zu (reaktiver Store), was sonst
    // ein doppeltes Laden samt erneutem "Notizen werden geladen"-Flackern auslöst.
    if (animalId === this._loadedAnimalId) {
      return;
    }

    this._loadedAnimalId = animalId;
    this.loadNotes();
    this.loadExports();
  }

  loadNotes(): void {
    if (!this.animal?.id) {
      this.notes = [];
      this.recomputeLockedNoteIds();
      return;
    }

    this.loading = true;
    this._animalNoteRepository.getByAnimalId(this.animal.id)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((notes) => {
        this.notes = [...(notes ?? [])].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        this.recomputeLockedNoteIds();
        this.loading = false;
      });
  }

  loadExports(): void {
    if (!this.animal?.id) {
      this.exports = [];
      this.recomputeLockedNoteIds();
      return;
    }

    this.loadingExports = true;
    this._animalNoteRepository.getExportsByAnimalId(this.animal.id)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((exports) => {
        this.exports = [...(exports ?? [])].sort((a, b) => new Date(b.signedAt).getTime() - new Date(a.signedAt).getTime());
        this.recomputeLockedNoteIds();
        this.loadingExports = false;
      });
  }

  get filteredNotes(): AnimalNoteModel[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      return this.notes;
    }

    // Auf dem reinen Text suchen, nicht im HTML-Markup. Titel wird mitdurchsucht.
    return this.notes.filter(note => {
      const haystack = `${note.title ?? ''} ${this.toPlainText(note.description)}`.toLowerCase();
      return haystack.includes(term);
    });
  }

  get newNoteEmpty(): boolean {
    return !this.newTitle.trim() || this.isHtmlEmpty(this.newDescription);
  }

  get editNoteEmpty(): boolean {
    return !this.editTitle.trim() || this.isHtmlEmpty(this.editDescription);
  }

  /** Reiner Text einer (HTML-)Notiz, z. B. für Suche und kompakte Vorschauen. */
  toPlainText(value: string | null | undefined): string {
    if (!value) {
      return '';
    }

    const tmp = document.createElement('div');
    tmp.innerHTML = value;
    return (tmp.textContent || tmp.innerText || '').replace(/ /g, ' ').trim();
  }

  /** Geschützte Leerzeichen aus dem WYSIWYG entfernen, damit kein &nbsp; in der DB landet. */
  private sanitizeNoteHtml(value: string | null | undefined): string {
    if (!value) {
      return '';
    }

    return value.replace(/&nbsp;/gi, ' ').replace(/ /g, ' ');
  }

  /** Erkennt leere WYSIWYG-Inhalte wie <p><br></p>. */
  private isHtmlEmpty(value: string | null | undefined): boolean {
    return this.toPlainText(value).length === 0;
  }

  /** Offene (nicht gesperrte) Notizen der aktuellen Filteransicht – nur diese erscheinen in der Hauptliste. */
  get activeNotes(): AnimalNoteModel[] {
    return this.filteredNotes.filter(note => !this.isLocked(note));
  }

  /** Notizen, die zu einem bestimmten PDF-Export gehören (für die ausklappbare Darstellung in der Historie). */
  notesForExport(exportEntry: AnimalNoteExportModel): AnimalNoteModel[] {
    const ids = new Set(
      (exportEntry.noteIds ?? []).map(id => (typeof id === 'string' ? Number(id) : id))
    );

    return this.notes
      .filter(note => ids.has(note.id))
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  }

  createNote(): void {
    if (!this.animal?.id || this.newNoteEmpty) return;

    const title = this.newTitle.trim();
    const description = this.sanitizeNoteHtml(this.newDescription);
    this.saving = true;
    this._animalNoteRepository.create(this.animal.id, title, description)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((created) => {
        this.saving = false;
        if (!created) return;

        this.newTitle = '';
        this.newDescription = '';
        this.createNoteDialogVisible = false;
        this._toast.showSuccess(this._translateService.translate('animalNotes.toast.saveSuccess'));
        this.loadNotes();
      });
  }

  openCreateNoteDialog(): void {
    this.newTitle = '';
    this.newDescription = '';
    this.createNoteDialogVisible = true;
  }

  closeCreateNoteDialog(): void {
    if (this.saving) {
      return;
    }

    this.createNoteDialogVisible = false;
    this.newTitle = '';
    this.newDescription = '';
  }

  startEdit(note: AnimalNoteModel): void {
    if (this.isLocked(note)) return;

    this.editNoteId = note.id;
    this.editTitle = note.title ?? '';
    this.editDescription = note.description;
  }

  cancelEdit(): void {
    this.editNoteId = null;
    this.editTitle = '';
    this.editDescription = '';
  }

  saveEdit(note: AnimalNoteModel): void {
    if (this.isLocked(note) || this.editNoteEmpty) return;

    const title = this.editTitle.trim();
    const description = this.sanitizeNoteHtml(this.editDescription);
    this.saving = true;
    this._animalNoteRepository.update(note.id, title, description)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((updated) => {
        this.saving = false;
        if (!updated) return;

        this.cancelEdit();
        this._toast.showSuccess(this._translateService.translate('animalNotes.toast.saveSuccess'));
        this.loadNotes();
      });
  }

  openDeleteDialog(note: AnimalNoteModel): void {
    if (this.isLocked(note)) return;

    this.deleteNote = note;
    this.deleteDialogVisible = true;
  }

  closeDeleteDialog(): void {
    if (this.deleting) {
      return;
    }

    this.deleteDialogVisible = false;
    this.deleteNote = null;
  }

  confirmDelete(): void {
    const note = this.deleteNote;
    if (!note || this.isLocked(note)) return;

    this.deleting = true;
    this._animalNoteRepository.delete(note.id)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((deleted) => {
        this.deleting = false;
        if (!deleted) return;

        this.selectedNoteIds = this.selectedNoteIds.filter(id => id !== note.id);
        if (this.editNoteId === note.id) {
          this.cancelEdit();
        }
        this.deleteDialogVisible = false;
        this.deleteNote = null;
        this._toast.showSuccess(this._translateService.translate('animalNotes.toast.deleteSuccess'));
        this.loadNotes();
      });
  }

  copyNote(note: AnimalNoteModel): void {
    if (!this.animal?.id) return;

    this.saving = true;
    this._animalNoteRepository.create(this.animal.id, (note.title ?? '').trim(), this.sanitizeNoteHtml(note.description))
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((copied) => {
        this.saving = false;
        if (!copied) return;

        this._toast.showSuccess(this._translateService.translate('animalNotes.toast.copySuccess'));
        this.loadNotes();
      });
  }

  toggleNoteSelection(noteId: number, checked: boolean): void {
    if (checked) {
      this.selectedNoteIds = Array.from(new Set([...this.selectedNoteIds, noteId]));
      return;
    }

    this.selectedNoteIds = this.selectedNoteIds.filter(id => id !== noteId);
  }

  isSelected(noteId: number): boolean {
    return this.selectedNoteIds.includes(noteId);
  }

  /** Auswählbare (nicht gesperrte) Notizen der aktuellen Filteransicht. */
  get selectableNoteIds(): number[] {
    return this.filteredNotes.filter(note => !this.isLocked(note)).map(note => note.id);
  }

  get allSelectableSelected(): boolean {
    const selectable = this.selectableNoteIds;
    return selectable.length > 0 && selectable.every(id => this.selectedNoteIds.includes(id));
  }

  toggleSelectAll(checked: boolean): void {
    if (checked) {
      this.selectedNoteIds = Array.from(new Set([...this.selectedNoteIds, ...this.selectableNoteIds]));
      return;
    }

    const selectable = new Set(this.selectableNoteIds);
    this.selectedNoteIds = this.selectedNoteIds.filter(id => !selectable.has(id));
  }

  clearSelection(): void {
    this.selectedNoteIds = [];
  }

  canExportSelected(): boolean {
    return this.selectedNoteIds.length > 0 && !this.selectedNoteIds.some(id => this._lockedNoteIds.has(id));
  }

  openBulkExportDialog(): void {
    this.exportNoteIds = this.selectedNoteIds.filter(id => !this._lockedNoteIds.has(id));
    this.openExportDialog();
  }
  private openExportDialog(): void {
    if (!this.exportNoteIds.length) {
      return;
    }

    this.exportSignedAt = new Date();
    this.exportSignatureDataUrl = null;
    this.exportLockAcknowledged = false;
    this.renderSignaturePad = false;
    this.exportDialogVisible = true;
  }

  onExportDialogShow(): void {
    this.renderSignaturePad = true;
  }

  canConfirmExport(): boolean {
    return !!this.exportSignatureDataUrl && this.exportLockAcknowledged && this.exportNoteIds.length > 0;
  }

  exportSelectedNotes(): void {
    const noteIds = this.exportNoteIds.filter(id => !this._lockedNoteIds.has(id));

    if (!this.animal?.id || !noteIds.length || !this.exportSignatureDataUrl || !this.exportSignedAt) {
      this._toast.showError(this._translateService.translate('animalNotes.toast.signatureMissing'));
      return;
    }

    this.finalizing = true;
    this._userRepository.getActiveUser().pipe(take(1), takeUntilDestroyed(this._destroyRef)).subscribe((activeUser) => {
      const creatorId = activeUser?.id;
      if (!creatorId) {
        this.finalizing = false;
        this._toast.showError(this._translateService.translate('animalNotes.toast.exportError'));
        return;
      }

      const payload: AnimalNoteExportPayload = {
        noteIds,
        creatorId,
        signature: this.exportSignatureDataUrl!,
        signedAt: this.exportSignedAt.toISOString()
      };

      this._animalNoteRepository.exportPdf(this.animal!.id, payload)
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe((pdfBlob) => {
          this.finalizing = false;

          if (!pdfBlob) {
            this._toast.showError(this._translateService.translate('animalNotes.toast.exportError'));
            return;
          }

          this.downloadPdf(pdfBlob, `tier-${this.animal?.id ?? 'tier'}-notizen-export.pdf`);
          noteIds.forEach(id => this._lockedNoteIds.add(id));
          this.selectedNoteIds = this.selectedNoteIds.filter(id => !noteIds.includes(id));
          this.exportNoteIds = [];
          this._toast.showSuccess(this._translateService.translate('animalNotes.toast.exportSuccess'));
          this.closeExportDialog();
          this.loadNotes();
          this.loadExports();
        });
    });
  }

  closeExportDialog(): void {
    this.exportDialogVisible = false;
    this.renderSignaturePad = false;
    this.exportSignatureDataUrl = null;
    this.exportLockAcknowledged = false;
  }

  isLocked(note: AnimalNoteModel): boolean {
    return this._lockedNoteIds.has(note.id);
  }

  get exportNotes(): AnimalNoteModel[] {
    return this.notes.filter(note => this.exportNoteIds.includes(note.id));
  }

  exportNotesLabel(noteIds: Array<number | string>): string {
    return noteIds.map(String).join(', ');
  }

  openExportedPdf(exportEntry: AnimalNoteExportModel): void {
    const fileName = this.extractExportFileName(exportEntry.downloadUrl || exportEntry.reportPdfPath || '');
    const url = this.buildExportPdfUrl(exportEntry);

    if (!url || !fileName) {
      this._toast.showError(this._translateService.translate('animalNotes.toast.exportLoadError'));
      return;
    }

    this.pdfPreviewVisible = true;
    this.pdfPreviewLoading = true;
    this.pdfPreviewData = null;
    this.pdfPreviewTitle = fileName;
    this.pdfPreviewFileName = fileName.toLowerCase().endsWith('.pdf') ? fileName : `${fileName}.pdf`;

    this._http.get(url, {responseType: 'blob'}).pipe(take(1), takeUntilDestroyed(this._destroyRef)).subscribe({
      next: (blob) => {
        this.pdfPreviewData = blob;
        this.pdfPreviewLoading = false;
      },
      error: () => {
        this.closePdfPreview();
        this._toast.showError(this._translateService.translate('animalNotes.toast.exportLoadError'));
      }
    });
  }

  closePdfPreview(): void {
    this.pdfPreviewVisible = false;
    this.pdfPreviewLoading = false;
    this.pdfPreviewData = null;
    this.pdfPreviewTitle = '';
    this.pdfPreviewFileName = 'export.pdf';
  }

  downloadPreviewPdf(): void {
    if (!this.pdfPreviewData) {
      return;
    }

    this.downloadPdf(this.pdfPreviewData, this.pdfPreviewFileName);
  }

  printPreviewPdf(): void {
    if (!this.pdfPreviewData) {
      return;
    }

    const url = URL.createObjectURL(this.pdfPreviewData);
    const iframe = document.createElement('iframe');

    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.src = url;

    iframe.onload = () => {
      const frameWindow = iframe.contentWindow;
      if (!frameWindow) {
        URL.revokeObjectURL(url);
        iframe.remove();
        return;
      }

      frameWindow.focus();
      frameWindow.print();

      setTimeout(() => {
        URL.revokeObjectURL(url);
        iframe.remove();
      }, 1000);
    };

    document.body.appendChild(iframe);
  }

  private buildExportPdfUrl(exportEntry: AnimalNoteExportModel): string | null {
    const rawPath = (exportEntry.downloadUrl || exportEntry.reportPdfPath || '').trim();
    const fileName = this.extractExportFileName(rawPath);

    if (!fileName) {
      return null;
    }

    return this._assetService.getAssetUrl(`/assets/${encodeURIComponent(fileName)}`);
  }

  private extractExportFileName(path: string): string | null {
    if (!path) {
      return null;
    }

    let normalizedPath = path.trim();

    try {
      normalizedPath = new URL(normalizedPath).pathname;
    } catch {
      // Relative Pfade bleiben unverändert.
    }

    normalizedPath = normalizedPath
      .split('?')[0]
      .replaceAll('\\', '/')
      .replace(/\/+$/, '');

    const pathSegments = normalizedPath.split('/');
    let fileName: string | null = null;

    for (let index = pathSegments.length - 1; index >= 0; index--) {
      const segment = pathSegments[index]?.trim();
      if (segment) {
        fileName = segment;
        break;
      }
    }

    if (!fileName) {
      return null;
    }

    try {
      return decodeURIComponent(fileName);
    } catch {
      return fileName;
    }
  }

  private recomputeLockedNoteIds(): void {
    this._lockedNoteIds.clear();

    for (const exportEntry of this.exports ?? []) {
      for (const noteId of exportEntry.noteIds ?? []) {
        const parsed = typeof noteId === 'string' ? Number(noteId) : noteId;
        if (!Number.isNaN(parsed)) {
          this._lockedNoteIds.add(parsed);
        }
      }
    }

    for (const note of this.notes ?? []) {
      if (this.hasLockFields(note)) {
        this._lockedNoteIds.add(note.id);
      }
    }
  }

  private hasLockFields(note: AnimalNoteModel): boolean {
    return (
      !!note.locked ||
      !!note.isLocked ||
      !!note.pdfPath ||
      !!note.signedPdfPath ||
      !!note.finalizedAt ||
      !!note.signedAt
    );
  }

  private downloadPdf(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');

    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }
}
